import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaMicrophone, FaMicrophoneSlash, FaTimes } from 'react-icons/fa';
import useVoiceRecognition from '../../hooks/useVoiceRecognition';
import { parseVoiceCommand } from '../../util/voiceCommandParser';
import { useLanguage } from '../../context/LanguageContext';
import { getAccounts } from '../../util/cacheManager';
import serverClient from '../../api/ServerClient';
import { getTranslation } from '../../config/translations';
import AddTransactionModal from '../Modals/AddTransaction';
import SuccessToast from '../../toast/SuccessToast';
import styles from './VoiceCommandButton.module.css';

const VoiceCommandButton = ({ refreshHome }) => {
    const { t, language } = useLanguage();
    const {
        transcript,
        error: speechError,
        isSupported,
        isSafariFallback,
        startListening,
        stopListening,
        resetTranscript,
    } = useVoiceRecognition(language);

    const [state, setState] = useState('idle');
    const [statusMessage, setStatusMessage] = useState('');
    const [lastTranscript, setLastTranscript] = useState('');
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [voicePrefilledData, setVoicePrefilledData] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const categoriesRef = useRef([]);
    const timeoutRef = useRef(null);

    // Fetch categories once on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await serverClient.get('/api/user/categories');
                categoriesRef.current = response?.data?.categories || [];
            } catch (e) {
                /* silently fail */
            }
        };
        fetchCategories();
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    // Handle toast visibility
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Keep tracking latest transcript while listening
    useEffect(() => {
        if (transcript && state === 'listening') {
            setLastTranscript(transcript);
        }
    }, [transcript, state]);

    const autoResetAfterDelay = useCallback(
        (delay = 3000) => {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setState('idle');
                setStatusMessage('');
                setLastTranscript('');
                resetTranscript();
            }, delay);
        },
        [resetTranscript]
    );

    // Handle speech errors
    useEffect(() => {
        if (speechError && state === 'listening') {
            setState('error');
            if (speechError === 'not-allowed') {
                setStatusMessage(t('voiceMicPermission'));
            } else if (speechError === 'no-speech') {
                setStatusMessage(t('voiceNoSpeech'));
            } else if (speechError === 'network' || speechError === 'service-not-allowed') {
                setStatusMessage(t('voiceNetworkError'));
            } else {
                setStatusMessage(t('voiceTryAgain'));
            }
            autoResetAfterDelay();
        }
    }, [speechError, state, t, autoResetAfterDelay]);

    const processCommandFromRef = useCallback(() => {
        const textToProcess = lastTranscriptRef.current || '';
        if (textToProcess.trim()) {
            handleProcessCommand(textToProcess.trim());
        } else {
            setState('idle');
            resetTranscript();
            setLastTranscript('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetTranscript]);

    const lastTranscriptRef = useRef('');
    useEffect(() => {
        lastTranscriptRef.current = lastTranscript;
    }, [lastTranscript]);

    const handleMicClick = useCallback(() => {
        if (state === 'listening') {
            // Manual stop — user tapped again to force-stop early
            stopListening();
            processCommandFromRef();
            return;
        }

        // Start fresh listening with auto-stop callback
        resetTranscript();
        setLastTranscript('');
        setStatusMessage('');
        setState('listening');
        startListening(() => {
            // This fires when silence is detected after user spoke
            processCommandFromRef();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, startListening, stopListening, resetTranscript, processCommandFromRef]);

    const handleProcessCommand = async (text) => {
        setState('processing');
        const accounts = await getAccounts();
        const parsed = parseVoiceCommand(text, language, categoriesRef.current, accounts || []);

        // On Safari fallback, user speaks English — show English hints regardless of app language
        const voiceT = isSafariFallback ? (key) => getTranslation(key, 'en') : t;

        // Check minimum requirements
        if (parsed.missing.includes('amount')) {
            setState('error');
            setStatusMessage(voiceT('voiceMissingAmount'));
            autoResetAfterDelay();
            return;
        }

        // For expense/income: Category is required
        if (
            parsed.missing.includes('category') &&
            (parsed.transactionType === 'expense' || parsed.transactionType === 'income')
        ) {
            setState('error');
            setStatusMessage(`${voiceT('voiceMissingCategory')}: "${text}"`);
            autoResetAfterDelay(4000);
            return;
        }

        // For transfer/withdraw: Only amount is required, user can add accounts in modal
        // No additional validation needed - accounts are optional in voice command

        // If all requirements met, open transaction modal with pre-filled data
        setState('idle');
        setVoicePrefilledData(parsed);
        setShowTransactionModal(true);
    };

    const handleModalClose = () => {
        setShowTransactionModal(false);
        setVoicePrefilledData(null);
        // Clear transcript ref so stale data cannot reopen the modal
        lastTranscriptRef.current = '';
        setLastTranscript('');
        resetTranscript();
    };

    const handleTransactionAdd = () => {
        // Modal will handle the transaction creation
        // Just close the modal and refresh
        handleModalClose();
        if (refreshHome) refreshHome();
    };

    if (!isSupported) return null;

    const getButtonClass = () => {
        const classes = [styles.micButton];
        if (state === 'listening') classes.push(styles.active);
        if (state === 'processing') classes.push(styles.processing);
        if (state === 'error') classes.push(styles.error);
        return classes.join(' ');
    };

    const getButtonIcon = () => {
        if (state === 'processing') return <div className={styles.spinner} />;
        if (state === 'error') return <FaTimes />;
        if (state === 'listening') return <FaMicrophoneSlash />;
        return <FaMicrophone />;
    };

    return (
        <div className={styles.voiceContainer}>
            {/* Status message bubble */}
            {statusMessage && state === 'error' && (
                <div
                    className={`${styles.statusMessage} ${styles.errorMsg}`}
                >
                    {statusMessage}
                </div>
            )}

            {/* Transcript bubble while listening / processing */}
            {(state === 'listening' || (state === 'processing' && lastTranscript)) && (
                <div className={styles.transcriptBubble}>
                    <span className={styles.transcriptLabel}>
                        {state === 'processing' ? t('voiceProcessing') : t('voiceListening')}
                    </span>
                    {isSafariFallback && state === 'listening' && !transcript && (
                        <span
                            className={styles.transcriptText}
                            style={{ opacity: 0.6, fontSize: '11px' }}
                        >
                            Speak in English
                        </span>
                    )}
                    <span className={styles.transcriptText}>
                        {(state === 'listening' ? transcript || lastTranscript : lastTranscript) ||
                            (!isSafariFallback ? t('voiceTapToSpeak') : '')}
                    </span>
                    {state === 'listening' && (
                        <div className={styles.waveContainer}>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className={styles.waveBar} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Hint on idle */}
            {state === 'idle' && (
                <div className={styles.hintText}>
                    {language === 'bn'
                        ? '\uD83C\uDFA4 \u09AD\u09DF\u09C7\u09B8'
                        : '\uD83C\uDFA4 Voice'}
                </div>
            )}

            {/* The floating mic button */}
            <button
                className={getButtonClass()}
                onClick={handleMicClick}
                aria-label={state === 'listening' ? t('voiceStopListening') : t('voiceTapToSpeak')}
                disabled={state === 'processing'}
            >
                {getButtonIcon()}
            </button>

            {/* Transaction Modal with pre-filled voice data */}
            {showTransactionModal && voicePrefilledData && (
                <AddTransactionModal
                    show={showTransactionModal}
                    onClose={handleModalClose}
                    onTransactionAdd={handleTransactionAdd}
                    transactionType={voicePrefilledData.transactionType}
                    setShowToast={setShowToast}
                    voicePrefilledData={voicePrefilledData}
                />
            )}

            {/* Success Toast */}
            {showToast && (
                <SuccessToast
                    message={t('transactionSuccessful')}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default VoiceCommandButton;
